/**
 * LegalPageLayout - Wrapper for static legal/changelog pages.
 *
 * Provides optional last-updated date and brand typography
 * within `AnonymousPortalLayout`.
 */

import { AnonymousPortalLayout } from '@/components/common/AnonymousPortalLayout';
import { NS } from '@/i18n/namespaces';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

export interface LegalPageLayoutProps {
  /** ISO date string (e.g. "2025-07-01") */
  lastUpdated?: string;
  children: React.ReactNode;
  className?: string;
}

export const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({
  lastUpdated,
  children,
  className,
}) => {
  const { t } = useTranslation(NS.COMMON);

  return (
    <AnonymousPortalLayout mainClassName="flex-1 w-full">
      <article
        className={clsx(
          'max-w-3xl mx-auto px-4 py-8',
          'text-brand-body-primary',
          className
        )}
      >
        {lastUpdated && (
          <p className="text-xs text-brand-body-secondary mb-6 pb-4 border-b border-gray-200">
            {t('LegalPage_LastUpdated', 'Last updated:')}{' '}
            <time dateTime={lastUpdated}>{lastUpdated}</time>
          </p>
        )}
        <div className="text-sm">{children}</div>
      </article>
    </AnonymousPortalLayout>
  );
};

LegalPageLayout.displayName = 'LegalPageLayout';

export default LegalPageLayout;
